import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PasswordInput } from '../ui/password-input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, ArrowRight, Building2, BarChart3, Zap, CheckCircle2, Loader2, Mail, User, Phone, Briefcase } from 'lucide-react';
import { RegistrationWizard } from '../wizards/RegistrationWizard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseApiError = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || e).join(', ');
  return fallback;
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  
  const [liveStats, setLiveStats] = useState({ users: 82, buildings: 247 });
  
  // Fetch live stats
  useState(() => {
    axios.get(`${API}/auth/stats`).then(res => {
      if (res.data.total_users) setLiveStats(s => ({ ...s, users: 82 + res.data.total_users, buildings: 200 + res.data.total_buildings }));
    }).catch(() => {});
  }, []);

  // Demo request state
  const [demoData, setDemoData] = useState({ email: '', name: '', company: '', phone: '' });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const userData = await login(loginData.email, loginData.password);
      if (userData?.role === 'tenant') {
        navigate('/my-unit');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(parseApiError(err, 'Přihlášení se nezdařilo'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const userData = await register(registerData.email, registerData.password, registerData.name);
      if (userData?.role === 'tenant') {
        navigate('/my-unit');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(parseApiError(err, 'Registrace se nezdařila'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!demoData.email) return;
    setDemoLoading(true);
    setDemoResult(null);
    try {
      const res = await axios.post(`${API}/auth/request-demo`, demoData);
      setDemoResult(res.data);
    } catch (err) {
      setDemoResult({ success: false, message: parseApiError(err, 'Chyba při vytváření demo účtu') });
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Marketing Hero */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-[#0a1628] to-slate-900" />
        <img
          src="https://images.unsplash.com/photo-1664813954641-1ffcb7b55fd1?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Smart energy"
          className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-luminosity"
        />
        
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          {/* Top - Logo */}
          <div className="flex items-center justify-between">
            <span className="energomat-logo text-2xl">
              <span className="text-white">energo</span><span className="mat">mat</span>
            </span>
          </div>
          
          {/* Middle - Hero content */}
          <div className="max-w-lg flex flex-col" style={{ minHeight: '60vh' }}>
            <div>
              <div className="inline-block px-3 py-1 rounded-full bg-[#00A8E8]/20 border border-[#00A8E8]/30 mb-4">
                <span className="text-xs text-[#00A8E8] font-semibold">Ušetřete 15+ hodin měsíčně</span>
              </div>
              <h1 className="text-5xl font-heading font-extrabold text-white mb-4 leading-[1.08] tracking-tight">
                Konec ručním odečtům.<br/>
                <span className="text-[#00A8E8]">Konec chybám.</span>
              </h1>
              <p className="text-lg text-white/60 leading-relaxed">
                Automatizujte vyúčtování elektřiny, hlídejte úniky a vydělávejte na wallboxech.
              </p>
            </div>

            <div className="flex-1" />

            {/* Stats row - with live numbers */}
            <div>
              <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <p className="text-3xl font-bold text-green-400 kpi-value">{liveStats.buildings}</p>
                </div>
                <p className="text-xs text-white/40">budov online</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-white kpi-value mb-1">{liveStats.users}</p>
                <p className="text-xs text-white/40">správců</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-[#00A8E8] kpi-value mb-1">24/7</p>
                <p className="text-xs text-white/40">monitoring</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-amber-400 kpi-value mb-1">1,3</p>
                <p className="text-xs text-white/40">Kč/den za byt</p>
              </div>
            </div>

            {/* Feature list */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {[
                { icon: Zap, text: 'Automatické dálkové odečty', color: 'text-amber-400' },
                { icon: Building2, text: 'Hlídání úniků 24/7', color: 'text-red-400' },
                { icon: BarChart3, text: 'PDF vyúčtování s QR kódem', color: 'text-blue-400' },
                { icon: Zap, text: 'Wallbox — pasivní příjem', color: 'text-green-400' },
              ].map(({ icon: Icon, text, color }, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                  <span className="text-sm text-white/60">{text}</span>
                </div>
              ))}
            </div>
            </div>
          </div>
          
          {/* Bottom */}
          <div className="flex items-center justify-between text-[11px] text-white/30">
            <span>&copy; 2026 energomat s.r.o. | Huťská 160, Kladno</span>
            <span>portal.energomat.cloud</span>
          </div>
        </div>
      </div>

      {/* Right side - Forms */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-background">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <span className="energomat-logo text-3xl">
              <span className="energo">energo</span><span className="mat">mat</span>
            </span>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Přihlášení</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrace</TabsTrigger>
              <TabsTrigger value="demo" data-testid="demo-tab" className="text-[#00A8E8]">Demo</TabsTrigger>
            </TabsList>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* LOGIN TAB */}
            <TabsContent value="login">
              <Card className="border-0 shadow-none">
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="text-2xl font-heading">Vítejte zpět</CardTitle>
                  <CardDescription>Přihlaste se ke svému účtu</CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="vas@email.cz"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Heslo</Label>
                      <PasswordInput
                        id="login-password"
                        placeholder="********"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-primary hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Zapomenuté heslo?
                      </Link>
                    </div>
                    <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {loading ? 'Přihlašuji...' : 'Přihlásit se'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* REGISTER TAB - Wizard */}
            <TabsContent value="register">
              <RegistrationWizard
                loading={loading}
                onRegister={async (data) => {
                  setLoading(true);
                  setError('');
                  try {
                    const userData = await register(data.email, data.password, data.name, {
                      planned_buildings: data.planned_buildings,
                      planned_units: data.planned_units,
                      company: data.company,
                      phone: data.phone,
                      wants_wallbox: data.wants_wallbox,
                      planned_wallboxes: data.planned_wallboxes,
                    });
                    // After registration, redirect to Stripe to add card
                    try {
                      const { licenseApi } = await import('../../lib/api');
                      const originUrl = window.location.origin;
                      const res = await licenseApi.createSubscription(originUrl);
                      if (res.data?.checkout_url) {
                        window.location.href = res.data.checkout_url;
                        return;
                      }
                    } catch (stripeErr) {
                      console.error('Stripe redirect error:', stripeErr);
                    }
                    // Fallback: go to settings if Stripe fails
                    navigate('/settings?license=setup');
                  } catch (err) {
                    setError(parseApiError(err, 'Registrace se nezdařila'));
                  } finally {
                    setLoading(false);
                  }
                }}
                onDemoRequest={() => {
                  document.querySelector('[data-testid="demo-tab"]')?.click();
                }}
              />
            </TabsContent>

            {/* DEMO REQUEST TAB */}
            <TabsContent value="demo">
              <Card className="border-0 shadow-none">
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="text-2xl font-heading">Vyzkoušejte zdarma</CardTitle>
                  <CardDescription>
                    Získejte demo přístup na 5 dní. Přihlašovací údaje vám zašleme na email.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  {demoResult?.success ? (
                    <div className="space-y-4" data-testid="demo-success">
                      <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="space-y-1">
                          <p className="font-medium text-green-800 dark:text-green-200">{demoResult.message}</p>
                          {demoResult.demo_email && (
                            <div className="mt-3 space-y-2 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground w-14">Email:</span>
                                <code className="tech-data bg-muted px-2 py-0.5 rounded" data-testid="demo-email-result">{demoResult.demo_email}</code>
                              </div>
                              {demoResult.demo_password && (
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground w-14">Heslo:</span>
                                  <code className="tech-data bg-muted px-2 py-0.5 rounded" data-testid="demo-password-result">{demoResult.demo_password}</code>
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                Účet vyprší {new Date(demoResult.expires_at).toLocaleDateString('cs-CZ')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button 
                        className="w-full h-11" 
                        onClick={() => {
                          if (demoResult.demo_email) {
                            setLoginData({ email: demoResult.demo_email, password: demoResult.demo_password || '' });
                          }
                          setDemoResult(null);
                          // Switch to login tab
                          const loginTab = document.querySelector('[data-testid="login-tab"]');
                          if (loginTab) loginTab.click();
                        }}
                        data-testid="demo-go-to-login"
                      >
                        Přejít na přihlášení
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleDemoRequest} className="space-y-4">
                      <div className="space-y-2">
                          <Label htmlFor="demo-email">
                          <Mail className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                          Váš email *
                        </Label>
                        <Input
                          id="demo-email"
                          type="email"
                          placeholder="vas@email.cz"
                          value={demoData.email}
                          onChange={(e) => setDemoData({ ...demoData, email: e.target.value })}
                          required
                          data-testid="demo-email"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="demo-name">
                            <User className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                            Jméno
                          </Label>
                          <Input
                            id="demo-name"
                            placeholder="Jan Novak"
                            value={demoData.name}
                            onChange={(e) => setDemoData({ ...demoData, name: e.target.value })}
                            data-testid="demo-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="demo-company">
                            <Briefcase className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                            Firma
                          </Label>
                          <Input
                            id="demo-company"
                            placeholder="Název s.r.o."
                            value={demoData.company}
                            onChange={(e) => setDemoData({ ...demoData, company: e.target.value })}
                            data-testid="demo-company"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="demo-phone">
                          <Phone className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                          Telefon
                        </Label>
                        <Input
                          id="demo-phone"
                          type="tel"
                          placeholder="+420 xxx xxx xxx"
                          value={demoData.phone}
                          onChange={(e) => setDemoData({ ...demoData, phone: e.target.value })}
                          data-testid="demo-phone"
                        />
                      </div>
                      
                      {demoResult && !demoResult.success && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{demoResult.message}</AlertDescription>
                        </Alert>
                      )}
                      
                      <Button type="submit" className="w-full h-11" disabled={demoLoading} data-testid="demo-submit">
                        {demoLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {demoLoading ? 'Vytvářím demo...' : 'Získat demo přístup'}
                      </Button>
                      <p className="text-xs text-center text-muted-foreground">
                        Demo účet je aktivní 5 dní. Bez závazků.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
